
/// <reference path="JournalEntryHeaderView.ts" />
/// <reference path="JournalAccountEntryCell.ts" />

class JournalEntryListViewController extends MUIViewController implements JournalAccountEntryCellDelegate
{
    static newInstance() : JournalEntryListViewController {
        let vc = new JournalEntryListViewController("journal-entry-list-view");
        vc.initWithResource("layout/journal_entries/JournalEntryListView.html");
        return vc;
    }

    private addButton:MUIButton = null;
    private saveButton:MUIButton = null;
    private tableView:UITableView = null;

    private _journalEntries:AccountingJournalEntry[] = [];

    viewDidLoad(){
        super.viewDidLoad();

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, function(){
            this.addNewJournalEntry();
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, function(){
            DBHelper.saveMainContext();
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false) return;
        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    // MARK: - Add new journal entry

    private addNewJournalEntry(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let journalEntry = MIOEntityDescription.insertNewObjectForEntityForName("AccountingJournalEntry", ad.managedObjectContext) as AccountingJournalEntry;
        journalEntry.date = new Date();
        this._journalEntries.push(journalEntry);
        this.tableView.reloadData();
    }

    // MARK: - Table view data source

    numberOfSections(tableView:UITableView){
        let fetchedSections = this.fetchedResultsController?.sections?.length ?? 0;
        return fetchedSections + this._journalEntries.length;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        let fetchedSections = this.fetchedResultsController?.sections?.length ?? 0;

        if (section < fetchedSections) {
            let sec = this.fetchedResultsController.sections[section];
            return sec.numberOfObjects();
        }

        // New (unsaved) journal entry sections: show 1 add-entry cell
        let jeIndex = section - fetchedSections;
        let je = this._journalEntries[jeIndex];
        let entries = je.entries;
        return (entries ? entries.count : 0) + 1;
    }

    viewForHeaderInSection(tableView:UITableView, section:number){
        let header = (tableView as any).dequeueReusableHeaderWithIdentifier("JournalEntryHeader") as JournalEntryHeaderView;
        let fetchedSections = this.fetchedResultsController?.sections?.length ?? 0;

        if (section < fetchedSections) {
            let firstObject = this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(0, section)) as AccountingEntry;
            header.item = firstObject.journalEntry;
        } else {
            let jeIndex = section - fetchedSections;
            header.item = this._journalEntries[jeIndex];
        }

        return header;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let fetchedSections = this.fetchedResultsController?.sections?.length ?? 0;

        if (indexPath.section < fetchedSections) {
            let cell = tableView.dequeueReusableCellWithIdentifier("JournalAccountEntryCell") as JournalAccountEntryCell;
            let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as AccountingEntry;
            cell.item = item;
            return cell;
        }

        // New journal entry section
        let jeIndex = indexPath.section - fetchedSections;
        let je = this._journalEntries[jeIndex];
        let entries = je.entries;
        let entryCount = entries ? entries.count : 0;

        if (indexPath.row < entryCount) {
            let cell = tableView.dequeueReusableCellWithIdentifier("JournalAccountEntryCell") as JournalAccountEntryCell;
            let allEntries = entries.allObjects as AccountingEntry[];
            cell.item = allEntries[indexPath.row];
            return cell;
        }

        // Add cell
        let cell = tableView.dequeueReusableCellWithIdentifier("AddJournalAccountEntryCell") as JournalAccountEntryCell;
        cell.delegate = this;
        cell.journalEntry = je;
        return cell;
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let fetchedSections = this.fetchedResultsController?.sections?.length ?? 0;
        if (indexPath.section >= fetchedSections) {
            let jeIndex = indexPath.section - fetchedSections;
            let je = this._journalEntries[jeIndex];
            let entryCount = je.entries ? je.entries.count : 0;
            if (indexPath.row >= entryCount) return UITableViewCellEditingStyle.Insert;
        }
        return UITableViewCellEditingStyle.None;
    }

    commitEditingStyleForRowAtIndexPath(tableView:UITableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath) {
        if (editingStyle == UITableViewCellEditingStyle.Insert) {
            let cell = tableView.cellAtIndexPath(indexPath) as JournalAccountEntryCell;
            this.addEntryDidPress(cell, cell.journalEntry);
        }
    }

    // MARK: - JournalAccountEntryCellDelegate

    addEntryDidPress(cell:JournalAccountEntryCell, journalEntry:AccountingJournalEntry){
        if (cell.account == null) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let entry = MIOEntityDescription.insertNewObjectForEntityForName("AccountingEntry", ad.managedObjectContext) as AccountingEntry;

        entry.account = cell.account;
        entry.accountNumber = cell.account.prefix;
        entry.accountName = cell.account.name;
        entry.reference = cell.reference;
        entry.debitAmount = cell.debitAmount;
        entry.creditAmount = cell.creditAmount;
        entry.date = journalEntry.date ?? new Date();
        entry.orderKey = DBHelper.newSafeTimestamp();
        entry.journalEntry = journalEntry;

        this.tableView.reloadData();
    }

    // MARK: - Fetched results controller

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let sortDescriptors = [
            MIOSortDescriptor.sortDescriptorWithKey('journalEntry.date', false),
            MIOSortDescriptor.sortDescriptorWithKey('orderKey', true)
        ];

        let fetchRequest = DBHelper.listFetchRequestWithEntityName("AccountingEntry", sortDescriptors, "journalEntry != null");
        fetchRequest.relationshipKeyPathsForPrefetching = ["account", "journalEntry"];
        fetchRequest.fetchLimit = 100;

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, "journalEntry.identifier");
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller:MIOFetchedResultsController){
        this.tableView.reloadData();
    }
}
